/**
 * TanStack Query hooks for accounts + membership (AB#331) and seats (AB#334).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountsApi } from '../api/accounts';

export const accountKeys = {
  all: ['accounts'] as const,
  list: () => [...accountKeys.all, 'list'] as const,
  members: (accountPid: string) => [...accountKeys.all, accountPid, 'members'] as const,
  invitations: (accountPid: string) =>
    [...accountKeys.all, accountPid, 'invitations'] as const,
};

/** All accounts the current user belongs to. */
export function useAccounts() {
  return useQuery({
    queryKey: accountKeys.list(),
    queryFn: () => accountsApi.list(),
  });
}

/** Create a new account (caller becomes owner). */
export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => accountsApi.create(name),
    onSuccess: () => qc.invalidateQueries({ queryKey: accountKeys.all }),
  });
}

/** Roster of one account. */
export function useAccountMembers(accountPid: string) {
  return useQuery({
    queryKey: accountKeys.members(accountPid),
    queryFn: () => accountsApi.members(accountPid),
    enabled: !!accountPid,
  });
}

/**
 * Add a member by email (owner only; 402 seat_limit_reached at cap). Adds a
 * registered user directly or emails an invite — branch on `res.data?.invited`
 * (AB#451). Invalidating `accountKeys.all` refreshes members + invitations.
 */
export function useAddAccountMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountPid, email }: { accountPid: string; email: string }) =>
      accountsApi.addMember(accountPid, email),
    onSuccess: () => qc.invalidateQueries({ queryKey: accountKeys.all }),
  });
}

/** Pending invitations for one account (owner only). */
export function useAccountInvitations(accountPid: string) {
  return useQuery({
    queryKey: accountKeys.invitations(accountPid),
    queryFn: () => accountsApi.invitations(accountPid),
    enabled: !!accountPid,
  });
}

/** Revoke a pending invitation (owner only). */
export function useRevokeAccountInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountPid, invitationId }: { accountPid: string; invitationId: number }) =>
      accountsApi.revokeInvitation(accountPid, invitationId),
    onSuccess: (_data, { accountPid }) => {
      qc.invalidateQueries({ queryKey: accountKeys.invitations(accountPid) });
      qc.invalidateQueries({ queryKey: accountKeys.members(accountPid) });
    },
  });
}

/** Re-email a pending invitation (owner only, AB#1015); refreshes its expiry. */
export function useResendAccountInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountPid, invitationId }: { accountPid: string; invitationId: number }) =>
      accountsApi.resendInvitation(accountPid, invitationId),
    onSuccess: (_data, { accountPid }) => {
      qc.invalidateQueries({ queryKey: accountKeys.invitations(accountPid) });
    },
  });
}

/** Accept an invitation with its token (the invited user's own action). */
export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ token }: { token: string }) => accountsApi.acceptInvitation(token),
    onSuccess: () => qc.invalidateQueries({ queryKey: accountKeys.all }),
  });
}

/** Remove a member (owner only). */
export function useRemoveAccountMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountPid, userPid }: { accountPid: string; userPid: string }) =>
      accountsApi.removeMember(accountPid, userPid),
    onSuccess: () => qc.invalidateQueries({ queryKey: accountKeys.all }),
  });
}

/**
 * Leave an account you're a member of (AB#1018). Drops that account's cached
 * roster/invites and refetches the account list before resolving, so callers
 * awaiting `mutateAsync` see the list without it. Moving the active account off
 * it (and resetting its site data) is the caller's job.
 */
export function useLeaveAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountPid: string) => accountsApi.leave(accountPid),
    onSuccess: (_data, accountPid) => {
      qc.removeQueries({ queryKey: [...accountKeys.all, accountPid] });
      return qc.invalidateQueries({ queryKey: accountKeys.list() });
    },
  });
}

/** Fetch the $1.99/mo seat checkout URL for an account (owner only). */
export function useSeatCheckout() {
  return useMutation({
    mutationFn: (accountPid: string) => accountsApi.seatCheckout(accountPid),
  });
}

/**
 * Preview self-deletion of the current user (AB#454/#463 Danger Zone) — step 1,
 * no side effects. Returns the cancellations + message (or a 409 sole_owner_block).
 */
export function useSelfDeletePreview() {
  return useMutation({
    mutationFn: () => accountsApi.selfDeletePreview(),
  });
}

/**
 * Confirm + perform self-deletion (AB#454/#463 Danger Zone) — step 2. Cancels
 * billing (fail-closed) then soft-deletes the user; the session is dead after.
 */
export function useSelfDeleteConfirm() {
  return useMutation({
    mutationFn: () => accountsApi.selfDeleteConfirm(),
  });
}
